/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';

import { ScrollingModule } from '@angular/cdk/scrolling';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// angular material
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';

// third party
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { AngularSplitModule } from 'angular-split';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

// prime NG
import { ChartModule } from 'primeng/chart';

// project components
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EmployeeDetailComponent } from './setting/employee/employee-detail/employee-detail.component';
import { EmployeeComponent } from './setting/employee/employee.component';
import { ProjectComponent as ProjectSettinngComponent } from './setting/project/project.component';
import { SettingComponent } from './setting/setting.component';
import { DialogComponent } from './shared/dialog/dialog.component';

import { EmployeeSearchPipe } from './pipe/employee-search.pipe';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeDE from '@angular/common/locales/de';
import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  AngularBaseModule,
  AppConfigService,
  authStorageFactory,
  oauthModuleConfigFactory
} from '@epg/angular-base-lib';
import {
  EpgContentHeaderModule,
  EpgFrameModule,
  EpgPermissionsDirective,
  EpgSolution,
  EpgTableModule,
  I18N_NAMESPACE_COMMON,
  I18N_NAMESPACE_TENANTMANAGER,
  provideEpgI18n,
  provideEpgPrimeNgMaterial,
  provideEpgProductBranding,
  provideEpgProductContext,
  provideEpgTenantManagerClient,
  TenantService
} from '@epg/ng-components';
import { setAppInjector, WorkflowDesignerLibModule } from '@epg/workflow-designer-lib';
import { registerLicense } from '@syncfusion/ej2-base';
import { OAuthModule, OAuthModuleConfig, OAuthStorage } from 'angular-oauth2-oidc';
import { TenantMappingService } from 'src/services/tenant-mapping.service';
import { PermissionsDirective } from '../services/directives/permissions.directive';
import { GlobalErrorHandler } from '../services/error-handler/global-error-handler';
import { CandeactivateGuard } from '../services/guards/candeactivate.guard';
import { TrimStringDirective } from './directives/trimString/trim-string.directive';
import { HelpAndDocumentationComponent } from './help-and-documentation/help-and-documentation.component';
import { GalleryComponent } from './home/gallery/gallery.component';
import { HomeComponent } from './home/home.component';
import { ScheduledTaskDetailComponent } from './home/task-home/scheduled-task/scheduled-task-detail/scheduled-task-detail.component';
import { ScheduledTaskComponent } from './home/task-home/scheduled-task/scheduled-task.component';
import { TaskAssigneeDetailComponent } from './home/task-home/shared/task-assignee-detail/task-assignee-detail.component';
import { TaskHomeComponent } from './home/task-home/task-home.component';
import { ClaimMenuComponent } from './home/task-home/task/claim-menu/claim-menu.component';
import { ArrayItemComponent } from './home/task-home/task/task-detail/items/array-item/array-item.component';
import { BarcodeItemComponent } from './home/task-home/task/task-detail/items/barcode-item/barcode-item.component';
import { BooleanItemComponent } from './home/task-home/task/task-detail/items/boolean-item/boolean-item.component';
import { ExtendedImageItemComponent } from './home/task-home/task/task-detail/items/extended-image-item/extended-image-item.component';
import { ImageItemComponent } from './home/task-home/task/task-detail/items/image-item/image-item.component';
import { ImageUrlItemComponent } from './home/task-home/task/task-detail/items/image-url-item/image-url-item.component';
import { IntItemComponent } from './home/task-home/task/task-detail/items/int-item/int-item.component';
import { DoubleItemComponent } from './home/task-home/task/task-detail/items/double-item/double-item.component';
import { ObjectItemComponent } from './home/task-home/task/task-detail/items/object-item/object-item.component';
import { SignatureItemComponent } from './home/task-home/task/task-detail/items/signature-item/signature-item.component';
import { StringItemComponent } from './home/task-home/task/task-detail/items/string-item/string-item.component';
import { TimestampItemComponent } from './home/task-home/task/task-detail/items/timestamp-item/timestamp-item.component';
import { CameraRecordComponent } from './home/task-home/task/task-detail/record/items/camera-record/camera-record.component';
import { ChecklistRecordComponent } from './home/task-home/task/task-detail/record/items/checklist-record/checklist-record.component';
import { FormRecordComponent } from './home/task-home/task/task-detail/record/items/form-record/form-record.component';
import { InfoRecordComponent } from './home/task-home/task/task-detail/record/items/info-record/info-record.component';
import { InputRecordComponent } from './home/task-home/task/task-detail/record/items/input-record/input-record.component';
import { QuestionRecordComponent } from './home/task-home/task/task-detail/record/items/question-record/question-record.component';
import { ScannerRecordComponent } from './home/task-home/task/task-detail/record/items/scanner-record/scanner-record.component';
import { RecordIconTextComponent } from './home/task-home/task/task-detail/record/items/shared-ui/record-icon-text/record-icon-text.component';
import { RecordImageComponent } from './home/task-home/task/task-detail/record/items/shared-ui/record-image/record-image.component';
import { RecordTextComponent } from './home/task-home/task/task-detail/record/items/shared-ui/record-text/record-text.component';
import { RecordTitleComponent } from './home/task-home/task/task-detail/record/items/shared-ui/record-title/record-title.component';
import { SignatureRecordComponent } from './home/task-home/task/task-detail/record/items/signature-record/signature-record.component';
import { UnknownRecordComponent } from './home/task-home/task/task-detail/record/items/unknown-record/unknown-record.component';
import { RecordComponent } from './home/task-home/task/task-detail/record/record.component';
import { DatumViewerComponent } from './home/task-home/task/task-detail/task-datum-viewer/datum-viewer/datum-viewer.component';
import { TaskDatumViewerComponent } from './home/task-home/task/task-detail/task-datum-viewer/task-datum-viewer.component';
import { TaskDetailComponent } from './home/task-home/task/task-detail/task-detail.component';
import { TaskGeneralDataComponent } from './home/task-home/task/task-detail/task-general-data/task-general-data.component';
import { TaskManagerComponent } from './home/task-home/task/task-manager/task-manager.component';
import { TaskSchedulingComponent } from './home/task-home/task/task-manager/task-scheduling/task-scheduling.component';
import { TaskComponent } from './home/task-home/task/task.component';
import { TasksResolver } from './home/task-home/tasks-resolver.component';
import { EmployeeDashboardComponent } from './home/time-tracking/employee-dashboard/employee-dashboard.component';
import { TaskDashboardComponent } from './home/time-tracking/task-dashboard/task-dashboard.component';
import { TimeTrackingComponent } from './home/time-tracking/time-tracking.component';
import { WorkflowDashboardComponent } from './home/time-tracking/workflow-dashboard/workflow-dashboard.component';
import { WorkflowCreationComponent } from './home/workflow/workflow-creation/workflow-creation.component';
import { WorkflowDesignerPlaceholderComponent } from './home/workflow/workflow-designer-placeholder/workflow-designer-placeholder.component';
import {
  FilterIncidentsPipe,
  SendEmailActionComponent,
  ToAdditionalRecipientsPipe
} from './home/workflow/workflow-detail/workflow-actions-management/send-email-action/send-email-action.component';
import { WorkflowActionsManagementComponent } from './home/workflow/workflow-detail/workflow-actions-management/workflow-actions-management.component';
import { WorkflowStatusBadgeComponent } from './home/workflow/workflow-detail/workflow-badge/workflow-badge.component';
import { WorkflowDetailComponent } from './home/workflow/workflow-detail/workflow-detail.component';
import { WorkflowFilterToolbarComponent } from './home/workflow/workflow-detail/workflow-filter-toolbar/workflow-filter-toolbar.component';
import { WorkflowPropertyComponent } from './home/workflow/workflow-detail/workflow-property/workflow-property.component';
import { WorkflowComponent } from './home/workflow/workflow.component';
import { LoginComponent } from './login/login/login.component';
import { EmployeeGroupSearchPipe } from './pipe/employee-group-search.pipe';
import { TaskStatusIconPipe } from './pipe/task-status-icon.pipe';
import { EmployeeGroupDetailComponent } from './setting/employee-group/employee-group-detail/employee-group-detail.component';
import { EmployeeGroupComponent } from './setting/employee-group/employee-group.component';
import { AddEmployeeComponent } from './setting/employee/add-employee/add-employee.component';
import { AnimatedCounterComponent } from './shared/animated-counter/animated-counter.component';
import { OneappButtonComponent } from './shared/buttons/oneapp-button/oneapp-button.component';
import { AttachmentsDisplayComponent } from './shared/attachments-display/attachments-display.component';
import { ChipsAutocompleteComponent } from './shared/chips-autocomplete/chips-autocomplete.component';
import { GroupedChipsAutocompleteComponent } from './shared/grouped-chips-autocomplete/grouped-chips-autocomplete.component';
import { DashboardCardComponent } from './shared/dashboard-card/dashboard-card.component';
import { DatePropertyComponent } from './shared/date-property/date-property.component';
import { DateRangePickerComponent } from './shared/date-range-picker/date-range-picker.component';
import { FilterPanelComponent } from './shared/filter-panel/filter-panel.component';
import { EpgImageSliderComponent } from './shared/epg-image-slider/epg-image-slider.component';
import { ErrorDialogComponent } from './shared/error-dialog/error-dialog.component';
import { GanttChartComponent } from './shared/gantt-chart/gantt-chart.component';
import { ImageSliderComponent } from './shared/image-slider/image-slider.component';
import { MapComponent } from './shared/map/map.component';
import { NavLinkComponent } from './shared/nav-link/nav-link.component';
import { OneappHeaderComponent } from './shared/oneapp-header/oneapp-header.component';
import { OneappTaskImageGridComponent } from './shared/oneapp-task-image-grid/oneapp-task-image-grid.component';
import { StandardListItemComponent } from './shared/standard-list-item/standard-list-item.component';

registerLicense(
  'Mgo+DSMBaFt/QHRqVVhkVFpFdEBBXHxAd1p/VWJYdVt5flBPcDwsT3RfQF5jSn9VdkNmUHpcdHJSQw==;Mgo+DSMBPh8sVXJ0S0J+XE9AflRDX3xKf0x/TGpQb19xflBPallYVBYiSV9jS31TdURlWXlbeXFXQ2JaVA==;ORg4AjUWIQA/Gnt2VVhkQlFacldJXGFWfVJpTGpQdk5xdV9DaVZUTWY/P1ZhSXxQdkZiX39YdX1QRGVYVUE=;MTM0NTIzOEAzMjMwMmUzNDJlMzBRSVNCdkdmdncxL3pMeTIxUG1McE9HZDB6eGFXd0gwSGhkczBaZC9pdGVjPQ==;MTM0NTIzOUAzMjMwMmUzNDJlMzBheDFoblBIZHQwVHo1c1dnUVYzOHVlOVdjcGgyYVp1ckN6cHc3UHdhb25zPQ==;NRAiBiAaIQQuGjN/V0Z+WE9EaFtKVmJLYVB3WmpQdldgdVRMZVVbQX9PIiBoS35RdUVgWHlednFcQ2JaUExz;MTM0NTI0MUAzMjMwMmUzNDJlMzBPOFJuNXBKQzFFZDc4R1YzbVJXdjdTNkxDSmdrSkd1SWxraW5wT2NxTUprPQ==;MTM0NTI0MkAzMjMwMmUzNDJlMzBYRkZzdnpvRFoxbkVpRDhWK2lVWXVzUUNXb1ZIZDVUbm95Vk5NUEZ3TUg0PQ==;Mgo+DSMBMAY9C3t2VVhkQlFacldJXGFWfVJpTGpQdk5xdV9DaVZUTWY/P1ZhSXxQdkZiX39YdX1QRGZVU0E=;MTM0NTI0NEAzMjMwMmUzNDJlMzBVOGNQenA4QlNEU3ZUMGEyajVKaGwvYkNpQWc0SzZtdjJGZHRHR2N5UkFnPQ==;MTM0NTI0NUAzMjMwMmUzNDJlMzBXdmRaV0hjZHhVK2ZwOXZpNXUwdlRHOUxxTFFvS0FpUDArZzBjN1dYZGNNPQ==;MTM0NTI0NkAzMjMwMmUzNDJlMzBPOFJuNXBKQzFFZDc4R1YzbVJXdjdTNkxDSmdrSkd1SWxraW5wT2NxTUprPQ=='
);
registerLocaleData(localeDE);

@NgModule({
  declarations: [
    AppComponent,
    SettingComponent,
    EmployeeComponent,
    ProjectSettinngComponent,
    DialogComponent,
    EmployeeSearchPipe,
    EmployeeGroupSearchPipe,
    EmployeeDetailComponent,
    ErrorDialogComponent,
    TaskComponent,
    TaskDetailComponent,
    HomeComponent,
    WorkflowComponent,
    WorkflowCreationComponent,
    WorkflowDetailComponent,
    WorkflowFilterToolbarComponent,
    WorkflowActionsManagementComponent,
    SendEmailActionComponent,
    EmployeeGroupComponent,
    EmployeeGroupDetailComponent,
    LoginComponent,
    TaskStatusIconPipe,
    StringItemComponent,
    ArrayItemComponent,
    BarcodeItemComponent,
    ImageItemComponent,
    ImageUrlItemComponent,
    ObjectItemComponent,
    SignatureItemComponent,
    GalleryComponent,
    OneappTaskImageGridComponent,
    AttachmentsDisplayComponent,
    ClaimMenuComponent,
    ExtendedImageItemComponent,
    AddEmployeeComponent,
    ImageSliderComponent,
    PermissionsDirective,
    TimeTrackingComponent,
    WorkflowDashboardComponent,
    EmployeeDashboardComponent,
    TaskDashboardComponent,
    TaskManagerComponent,
    TaskDatumViewerComponent,
    IntItemComponent,
    DoubleItemComponent,
    BooleanItemComponent,
    TimestampItemComponent,
    TrimStringDirective,
    TaskHomeComponent,
    ScheduledTaskComponent,
    ScheduledTaskDetailComponent,
    TasksResolver,
    RecordComponent,
    QuestionRecordComponent,
    RecordTitleComponent,
    RecordTextComponent,
    RecordImageComponent,
    InfoRecordComponent,
    UnknownRecordComponent,
    DatumViewerComponent,
    TaskAssigneeDetailComponent,
    CameraRecordComponent,
    RecordIconTextComponent,
    NavLinkComponent,
    TaskAssigneeDetailComponent,
    InputRecordComponent,
    ChecklistRecordComponent,
    SignatureRecordComponent,
    ScannerRecordComponent,
    MapComponent,
    TaskGeneralDataComponent,
    OneappHeaderComponent,
    TaskSchedulingComponent,
    DatePropertyComponent,
    ToAdditionalRecipientsPipe,
    ChipsAutocompleteComponent,
    GroupedChipsAutocompleteComponent,
    StandardListItemComponent,
    OneappButtonComponent,
    EpgImageSliderComponent,
    DashboardCardComponent,
    OneappTaskImageGridComponent,
    WorkflowDesignerPlaceholderComponent,
    GanttChartComponent,
    AnimatedCounterComponent,
    FilterIncidentsPipe,
    FormRecordComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    ScrollingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    LayoutModule,
    DragDropModule,
    MatToolbarModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    MatInputModule,
    MatMenuModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatDialogModule,
    MatSelectModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatTableModule,
    MatDatepickerModule,
    NgxMaterialTimepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatCardModule,
    MatProgressBarModule,
    MatGridListModule,
    MatSortModule,
    MatTooltipModule,
    MatStepperModule,
    MatTabsModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    NgxJsonViewerModule,
    VirtualScrollerModule,
    AngularSplitModule,
    AngularBaseModule,
    OAuthModule.forRoot(),
    ChartModule,
    ChartModule,
    EpgFrameModule,
    EpgContentHeaderModule,
    EpgTableModule,
    WorkflowDesignerLibModule,
    EpgPermissionsDirective,
    HelpAndDocumentationComponent,
    ClipboardModule,
    DateRangePickerComponent,
    FilterPanelComponent,
    WorkflowStatusBadgeComponent,
    WorkflowPropertyComponent
  ],
  providers: [
    provideEpgProductBranding('ONE APP', EpgSolution.EPG_ONE),
    provideEpgPrimeNgMaterial(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: OAuthStorage,
      useFactory: () => authStorageFactory('TestAPP')
    },
    {
      provide: OAuthModuleConfig,
      deps: [AppConfigService],
      useFactory: oauthModuleConfigFactory
    },
    provideEpgTenantManagerClient(
      (configService) => ({
        baseUrl: configService.getParameter('TenantManagerEndpoint') as string
      }),
      [AppConfigService]
    ),
    provideEpgProductContext('oneapp'),
    provideEpgI18n({
      ns: [I18N_NAMESPACE_COMMON, I18N_NAMESPACE_TENANTMANAGER],
      supportedLngs: ['en', 'de', 'fr', 'es', 'ja-JP', 'xxa']
    }),
    CandeactivateGuard,
    {
      provide: TenantService,
      useExisting: TenantMappingService
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor(injector: Injector) {
    setAppInjector(injector);
  }
}
